import {
  CalendarPlus,
  Eye,
  FilePlus2,
  Lock,
  Receipt,
  Stethoscope,
  UserPlus,
  Wallet,
  type LucideIcon,
} from "lucide-react";

import type { MeResponse } from "@/features/auth/types";
import { can, showsOptical } from "@/lib/permissions/can";

/**
 * Registre des ACTIONS de la palette de commandes.
 *
 * La navigation seule ne suffit plus : cinq groupes de menu et une trentaine
 * d'entrées, il faut savoir dans lequel vit « clôturer la caisse » avant de
 * pouvoir le faire. Une action nommée par son verbe supprime cette étape.
 *
 * Source unique : le tableau de bord consomme le même registre pour ses
 * raccourcis, ce qui garantit qu'une action ne peut pas exister à un endroit
 * et manquer à l'autre.
 *
 * `?nouveau=1` : convention de deep-link déjà en place — la page cible lit le
 * paramètre au montage et ouvre son formulaire de création.
 */
export interface Command {
  /** Identifiant stable — sert de clé aux commandes récentes. */
  id: string;
  /** Formulé à l'infinitif ou en substantif d'action, comme le bouton visé. */
  label: string;
  to: string;
  icon: LucideIcon;
  /** Permission requise. Absente = visible dès qu'une clinique est active. */
  permission?: string;
  /** Termes de recherche supplémentaires (synonymes, vocabulaire du terrain). */
  keywords?: string;
  /** Réservé aux cliniques de spécialité « optique ». */
  requiresOptical?: boolean;
  /** Masqué si la clinique ne suit pas le stock. */
  requiresStock?: boolean;
  /** Proposé comme raccourci sur le tableau de bord. */
  onDashboard?: boolean;
}

export const COMMANDS: Command[] = [
  {
    id: "patient.create",
    label: "Nouveau patient",
    to: "/patients?nouveau=1",
    icon: UserPlus,
    permission: "patients.create",
    keywords: "dossier fiche inscrire enregistrer",
    onDashboard: true,
  },
  {
    id: "consultation.create",
    label: "Nouvelle consultation",
    to: "/consultations?nouveau=1",
    icon: Stethoscope,
    permission: "consultations.create",
    keywords: "acte visite examiner medecin",
  },
  {
    id: "appointment.create",
    label: "Planifier un rendez-vous",
    to: "/rendez-vous?nouveau=1",
    icon: CalendarPlus,
    permission: "appointments.create",
    keywords: "rdv agenda planning caler",
    onDashboard: true,
  },
  {
    id: "sale.create",
    label: "Nouvelle facture",
    to: "/factures/nouvelle",
    icon: FilePlus2,
    permission: "sales.create",
    keywords: "vente facturer encaissement proforma",
    onDashboard: true,
  },
  {
    id: "payment.create",
    label: "Encaisser un paiement",
    to: "/paiements?nouveau=1",
    icon: Wallet,
    permission: "payments.create",
    keywords: "regler caisse versement acompte",
  },
  {
    id: "cash_closure.create",
    label: "Clôturer la caisse du jour",
    to: "/caisse/cloture",
    icon: Lock,
    permission: "cash_closure.create",
    keywords: "caisse fermeture journee arrete comptage",
  },
  {
    id: "eye_prescription.create",
    label: "Nouvelle ordonnance optique",
    // Cette page utilise `?new=1` (flux existant depuis la consultation).
    to: "/eye-prescriptions?new=1",
    icon: Eye,
    permission: "eye_prescriptions.create",
    keywords: "lunettes verres correction vue",
    requiresOptical: true,
  },
  {
    id: "prescription.create",
    label: "Nouvelle ordonnance",
    to: "/prescriptions?nouveau=1",
    icon: Receipt,
    permission: "prescriptions.create",
    keywords: "medicament traitement prescrire",
  },
];

/**
 * Une action est-elle proposable à cet utilisateur ?
 *
 * INV-6 : une action dont la permission manque est MASQUÉE, jamais désactivée.
 * Le back revérifie de toute façon — ce filtre sert l'ergonomie, pas la
 * sécurité.
 */
export function isCommandVisible(me: MeResponse | null | undefined, command: Command): boolean {
  const user = me ?? null;
  return (
    // Sans clinique active, aucune création n'a de rattachement possible.
    !!user?.active_organisation_id &&
    (!command.permission || can(user, command.permission)) &&
    (!command.requiresOptical || showsOptical(user ?? null)) &&
    (!command.requiresStock || !!user?.active_stock_tracking)
  );
}

export function commandsFor(me: MeResponse | null | undefined): Command[] {
  return COMMANDS.filter((command) => isCommandVisible(me, command));
}

/** Raccourcis du tableau de bord — sous-ensemble marqué du même registre. */
export function dashboardCommandsFor(me: MeResponse | null | undefined): Command[] {
  return commandsFor(me).filter((command) => command.onDashboard);
}
